import type { CSSProperties, MouseEvent as ReactMouseEvent } from "react";
import type { TableColumn } from "../../lib/v3-columns";
import { formatCellValue, formatRelativeTime } from "../../lib/v3-columns";
import { EntityChip } from "../entity/EntityChip";

const ENUM_COLORS: Record<string, { bg: string; fg: string }> = {
  discovery: { bg: "#FAEEDA", fg: "#854F0B" },
  proposal: { bg: "#EEEDFE", fg: "#3C3489" },
  negotiation: { bg: "#E1F5EE", fg: "#0F6E56" },
  "closed won": { bg: "#E1F5EE", fg: "#0F6E56" },
  "closed lost": { bg: "var(--c-red-50)", fg: "var(--c-red-800)" },
};

interface FieldCellProps {
  value: unknown;
  column: TableColumn;
  isEditing: boolean;
  isMutating: boolean;
  onStartEdit: () => void;
  /** Sprint 12 — resolve an FK id to its display value for inline chips. */
  resolveRef?: (id: string) => string | undefined;
  /** Sprint 12b — this column is the dedicated side-panel trigger (the ID column). */
  isIdColumn?: boolean;
  /** Sprint 12b — open the row-detail panel; wired only on the ID column. */
  onOpenDetail?: (e: ReactMouseEvent) => void;
  style?: CSSProperties;
}

export function FieldCell({
  value,
  column,
  isEditing,
  onStartEdit,
  resolveRef,
  isIdColumn,
  onOpenDetail,
  style,
}: FieldCellProps) {
  // Editable cells handle their own click (enter edit); stop it bubbling to the
  // row so editing a cell doesn't also open the row-detail panel.
  const handleEditClick = column.editable
    ? (e: ReactMouseEvent) => {
        e.stopPropagation();
        onStartEdit();
      }
    : undefined;
  const base: CSSProperties = {
    padding: 10,
    fontSize: 13,
    fontFamily: "var(--font-sans)",
    color: column.editable ? "var(--color-text-primary)" : "var(--color-text-secondary)",
    textAlign: column.align,
    fontWeight: column.isTitle ? 500 : 400,
    cursor: column.editable && !isEditing ? "text" : "default",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...(column.type === "number" || column.type === "currency"
      ? { fontVariantNumeric: "tabular-nums" as const }
      : {}),
    ...style,
  };

  if (column.type === "virtual-id") {
    const idText = value != null ? String(value) : "";
    if (isIdColumn) {
      // The ID column opens the row-detail panel: hover tint + trailing chevron
      // signal "click to open" (the .kuvio-id-cell rules drive the hover states).
      return (
        <td
          className="kuvio-id-cell"
          onClick={onOpenDetail}
          title="Open detail"
          style={{ ...base, fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--color-text-secondary)", cursor: "pointer", overflow: "visible" }}
        >
          <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{idText}</span>
            <i className="ti ti-chevron-right kuvio-id-chevron" style={{ fontSize: 13, flexShrink: 0 }} aria-hidden="true" />
          </span>
        </td>
      );
    }
    return (
      <td style={{ ...base, fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--color-text-secondary)", cursor: "default" }}>
        {idText}
      </td>
    );
  }

  if (column.type === "virtual-timestamp") {
    return (
      <td style={{ ...base, color: "var(--color-text-secondary)", cursor: "default" }}>
        {value != null ? formatRelativeTime(String(value)) : ""}
      </td>
    );
  }

  if (column.type === "enum" && value) {
    const key = String(value).toLowerCase();
    const colors = ENUM_COLORS[key];
    return (
      <td style={base} onClick={handleEditClick}>
        <span
          style={{
            fontSize: 11,
            padding: "2px 8px",
            background: colors?.bg ?? "var(--color-background-secondary)",
            color: colors?.fg ?? "var(--color-text-secondary)",
            borderRadius: 10,
            fontWeight: 500,
          }}
        >
          {String(value)}
        </span>
      </td>
    );
  }

  // Sprint 12 — reference cells render clickable entity chips (Notion
  // convergence). The chip stops propagation so it navigates to the FK target
  // rather than opening this row's detail.
  if (column.type === "reference" && value) {
    const id = String(value);
    return (
      <td style={base}>
        <EntityChip id={id} name={resolveRef?.(id) ?? id} typeSlug={column.target ?? ""} />
      </td>
    );
  }

  if (column.type === "references" && Array.isArray(value)) {
    return (
      <td style={base}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {value.map((v) => {
            const id = String(v);
            return <EntityChip key={id} id={id} name={resolveRef?.(id) ?? id} typeSlug={column.target ?? ""} />;
          })}
        </div>
      </td>
    );
  }

  if (column.type === "document") {
    return (
      <td style={base}>
        {value ? (
          <span style={{ color: "var(--color-text-info)", cursor: "pointer", fontSize: 12 }}>
            View document
          </span>
        ) : null}
      </td>
    );
  }

  const displayValue = column.format === "relative_date" && value
    ? formatRelativeTime(String(value))
    : formatCellValue(value, column);

  return (
    <td style={base} onClick={handleEditClick}>
      {displayValue}
    </td>
  );
}
